import {
  AccountInfo,
  BrokerConnector,
  Order,
  Position,
  TickerItem,
  OptionsChainResponse
} from './types'
import {
  getSecureItem,
  setSecureItem,
  removeSecureItem
} from './utils/secureStorage'

/**
 * Groww trading connector implementation
 * Provides integration with Groww's trading API for Indian stocks and mutual funds
 * 
 * This connector enables trading on NSE, BSE and other Indian exchanges through Groww's platform.
 * It supports various order types, market data retrieval, and account management features.
 * 
 * @example
 * // Initialize the connector with API credentials
 * const growwConnector = new GrowwConnector('your-api-key');
 * 
 * // Connect to Groww
 * await growwConnector.connect();
 * 
 * // Place a market order
 * const order = await growwConnector.placeOrder({
 *   symbol: 'RELIANCE',
 *   quantity: 1,
 *   type: 'MARKET',
 *   side: 'BUY'
 * });
 */
export class GrowwConnector implements BrokerConnector {
  private apiKey: string
  private isConnected: boolean = false

  constructor(apiKey: string) {
    // For Groww, we only need the API key (access token)
    this.apiKey = apiKey

    // Auto-connect if we have valid credentials
    if (typeof window !== 'undefined' && this.apiKey) {
      // Set isConnected to true if we have credentials from session storage
      const storedApiKey = getSecureItem('groww_api_key')

      if (storedApiKey === this.apiKey) {
        this.isConnected = true
        console.log('GrowwConnector initialized from stored credentials')
      }
    }
  }

  /**
   * Check if the connector is currently connected to Groww
   * Verifies if we have a valid authentication token for Groww
   * 
   * @returns {boolean} True if connected with a valid token, false otherwise
   * 
   * @example
   * if (growwConnector.isConnectedToBroker()) {
   *   // Proceed with trading operations
   * } else {
   *   // Show login UI
   * }
   */
  isConnectedToBroker(): boolean {
    return this.isConnected
  }

  /**
   * Connect to the Groww trading platform
   * Establishes a connection to Groww by validating the API token
   * 
   * @returns {Promise<boolean>} Promise resolving to true if connection is successful, false otherwise
   * @throws {Error} If API credentials are invalid or connection fails
   * 
   * @example
   * try {
   *   const connected = await growwConnector.connect();
   *   if (connected) {
   *     console.log('Connected to Groww');
   *   }
   * } catch (error) {
   *   console.error('Connection failed:', error);
   * }
   */
  async connect(): Promise<boolean> {
    try {
      // Validate that the API key is provided
      if (!this.apiKey) {
        throw new Error('API key (access token) is required')
      }

      // For Groww, we just need to validate the token
      // We'll make a simple API call to check if the token is valid
      const response = await fetch('/api/connectors/groww/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to connect to Groww')
      }

      // Store connection state in session storage
      if (typeof window !== 'undefined') {
        setSecureItem('groww_api_key', this.apiKey)
      }

      this.isConnected = true
      return true
    } catch (error) {
      console.error('Groww connection failed:', error)
      this.isConnected = false
      return false
    }
  }

  /**
   * Disconnect from the Groww trading platform
   * Clears stored credentials and resets the connection state
   * 
   * @returns {Promise<boolean>} Promise resolving to true if disconnection is successful
   * 
   * @example
   * await growwConnector.disconnect();
   * console.log('Disconnected from Groww');
   */
  async disconnect(): Promise<boolean> {
    try {
      // Clear stored credentials
      if (typeof window !== 'undefined') {
        removeSecureItem('groww_api_key')
      }

      this.isConnected = false
      return true
    } catch (error) {
      console.error('Groww disconnection failed:', error)
      return false
    }
  }

  /**
   * Place a trading order on Groww
   * Supports various order types including MARKET, LIMIT, and bracket orders
   * 
   * @param {Partial<Order>} order The order details including symbol, quantity, price, etc.
   * @returns {Promise<Order>} Promise resolving to the placed order with order ID and status
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * // Place a market buy order
   * const marketOrder = await growwConnector.placeOrder({
   *   symbol: 'RELIANCE',
   *   quantity: 1,
   *   type: 'MARKET',
   *   side: 'BUY',
   *   exchange: 'NSE'
   * });
   * 
   * @example
   * // Place a limit sell order
   * const limitOrder = await growwConnector.placeOrder({
   *   symbol: 'INFY',
   *   quantity: 10,
   *   price: 1500,
   *   type: 'LIMIT',
   *   side: 'SELL',
   *   exchange: 'NSE'
   * });
   */
  async placeOrder(order: Partial<Order>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      const { symbol, quantity, price, type, side, triggerPrice, exchange } = order

      if (!symbol || !quantity || !side) {
        throw new Error('Symbol, quantity, and side are required')
      }

      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          order: {
            trading_symbol: symbol,
            quantity,
            price,
            trigger_price: triggerPrice,
            validity: 'DAY',
            exchange: exchange || 'NSE', // Default to NSE if not provided
            segment: 'CASH', // Default to CASH
            product: 'CNC', // Default to CNC (delivery)
            order_type: type || 'MARKET', // Default to MARKET
            transaction_type: side // BUY or SELL
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to place order')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to place order:', error)
      throw error
    }
  }

  /**
   * Place a Good Till Triggered (GTT) order on Groww
   * GTT orders remain active until the specified trigger condition is met
   * Note: This is a mock implementation as Groww API may not directly support GTT orders
   * 
   * @param {Partial<Order>} order The GTT order details
   * @returns {Promise<Order>} Promise resolving to the placed GTT order
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * // Place a GTT buy order that triggers when price falls to a certain level
   * const gttBuyOrder = await growwConnector.placeGTT({
   *   symbol: 'HDFCBANK',
   *   quantity: 5,
   *   price: 1650,  // Limit price to buy at
   *   side: 'BUY',
   *   triggerPrice: 1700  // Trigger when price falls to 1700
   * });
   */
  async placeGTT(order: Partial<Order>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      const { symbol, quantity, price, side, triggerPrice, triggerType } = order

      if (!symbol || !quantity || !side || !triggerPrice) {
        throw new Error('Symbol, quantity, side, and trigger price are required for GTT orders')
      }

      // This is a mock implementation since Groww API docs don't explicitly mention GTT orders
      console.log('Mock GTT order placed with Groww:', { symbol, quantity, price, side, triggerPrice, triggerType })

      // Return a mock order response
      return {
        orderId: `GTT-MOCK-${Date.now()}`,
        symbol: symbol,
        quantity: quantity,
        price: price || triggerPrice,
        type: 'GTT',
        side: side,
        status: 'OPEN',
        triggerPrice: triggerPrice,
        triggerType: triggerType || 'SINGLE',
        product: 'CNC'
      }
    } catch (error) {
      console.error('Failed to place GTT order:', error)
      throw error
    }
  }

  /**
   * Get all orders from Groww
   * Retrieves all orders that have been placed
   * 
   * @returns {Promise<Order[]>} Promise resolving to an array of orders
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const orders = await growwConnector.getOrders();
   * console.log(`You have ${orders.length} orders`);
   */
  async getOrders(): Promise<Order[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch orders')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      return []
    }
  }

  /**
   * Get all positions from Groww
   * Retrieves all current positions with profit/loss values
   * 
   * @returns {Promise<Position[]>} Promise resolving to an array of positions
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const positions = await growwConnector.getPositions();
   * 
   * // Calculate total P&L
   * const totalPnL = positions.reduce((sum, position) => sum + position.pnl, 0);
   * console.log(`Total P&L: ₹${totalPnL}`);
   */
  async getPositions(): Promise<Position[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/positions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch positions')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch positions:', error)
      return []
    }
  }

  /**
   * Get account balance from Groww
   * Returns the available cash balance for trading
   * 
   * @returns {Promise<number>} Promise resolving to the account balance as a number
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const balance = await growwConnector.getBalance();
   * console.log(`Available balance: ₹${balance.toFixed(2)}`);
   */
  async getBalance(): Promise<number> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/margins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch balance')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch balance:', error)
      return 0
    }
  }

  /**
   * Get account information from Groww
   * Retrieves account details including permissions and features
   * 
   * @returns {Promise<AccountInfo>} Promise resolving to account information
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const accountInfo = await growwConnector.getAccountInfo();
   * console.log('Options trading enabled:', accountInfo.isOptionsEnabled);
   */
  async getAccountInfo(): Promise<AccountInfo> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch account info')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch account info:', error)
      return {} as AccountInfo
    }
  }

  /**
   * Get user's holdings from Groww
   * Retrieves all stocks owned by the user
   * 
   * @returns {Promise<any[]>} Promise resolving to an array of assets/holdings
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const holdings = await growwConnector.getUserAssets();
   * 
   * // Print all holdings
   * holdings.forEach(holding => {
   *   console.log(`${holding.tradingSymbol}: ${holding.quantity} shares, current value: ₹${holding.currentValue}`);
   * });
   */
  async getUserAssets(): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/holdings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch user assets')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch user assets:', error)
      return []
    }
  }

  /**
   * Get order history from Groww
   * Retrieves historical orders with their execution details
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of historical orders
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get all order history
   * const allOrders = await growwConnector.getOrderHistory();
   * 
   * // Get order history for a specific symbol
   * const relianceOrders = await growwConnector.getOrderHistory('RELIANCE');
   */
  async getOrderHistory(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/orderhistory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch order history')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch order history:', error)
      return []
    }
  }

  /**
   * Get trade history from Groww
   * Retrieves historical trades/executions
   * 
   * @param {string} [symbol] Optional symbol to filter trades by
   * @returns {Promise<any[]>} Promise resolving to an array of historical trades
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get all trade history
   * const allTrades = await growwConnector.getTradeHistory();
   * 
   * // Get trade history for a specific symbol
   * const relianceTrades = await growwConnector.getTradeHistory('RELIANCE');
   */
  async getTradeHistory(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/tradehistory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch trade history')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch trade history:', error)
      return []
    }
  }

  /**
   * Get open orders from Groww
   * Retrieves all orders that are currently open/pending
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of open orders
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get all open orders
   * const allOpenOrders = await growwConnector.getOpenOrders();
   * 
   * // Get open orders for a specific symbol
   * const relianceOpenOrders = await growwConnector.getOpenOrders('RELIANCE');
   */
  async getOpenOrders(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/openorders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch open orders')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch open orders:', error)
      return []
    }
  }

  /**
   * Get chart-formatted open orders from Groww
   * Retrieves open orders in a format suitable for chart display
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of chart-formatted orders
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get all chart orders
   * const chartOrders = await growwConnector.getChartOpenOrders();
   * 
   * // Get chart orders for a specific symbol
   * const relianceChartOrders = await growwConnector.getChartOpenOrders('RELIANCE');
   */
  async getChartOpenOrders(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Get open orders using the existing method
      const orders = await this.getOpenOrders(symbol)

      // Transform the orders to match the format expected by the chart component
      return orders.map(order => ({
        time: new Date(order.time || Date.now()).toISOString().replace('T', ' ').substring(0, 19),
        side: order.transaction_type || 'BUY',
        limitPrice: parseFloat(order.price || '0'),
        size: parseFloat(order.quantity || '0'),
        symbol: order.trading_symbol?.toLowerCase() || '',
        stopLoss: order.trigger_price ? parseFloat(order.trigger_price) : 0,
        takeProfit: 0, // Groww doesn't have take profit in the same way
        orderId: order.groww_order_id || ''
      }))
    } catch (error) {
      console.error('Failed to fetch chart open orders:', error)
      return []
    }
  }

  /**
   * Get market data for specified symbols
   * Retrieves current price, change percentage, and volume data
   * 
   * @param {Object} [options] Optional parameters for the request
   * @param {string} [options.symbol] Single symbol to fetch data for
   * @param {string[]} [options.symbols] Array of symbols to fetch data for
   * @returns {Promise<TickerItem[]>} Promise resolving to an array of ticker items
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get data for a single symbol
   * const relianceData = await growwConnector.getTickers({ symbol: 'RELIANCE' });
   * 
   * // Get data for multiple symbols
   * const data = await growwConnector.getTickers({ symbols: ['RELIANCE', 'TCS', 'INFY'] });
   */
  async getTickers(options?: { symbol?: string, symbols?: string[] }): Promise<TickerItem[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/tickers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbol: options?.symbol,
          symbols: options?.symbols
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch tickers')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch tickers:', error)
      return []
    }
  }

  /**
   * Get list of favorite symbols from Groww
   * Retrieves the user's watchlist/favorite symbols
   * 
   * @returns {Promise<string[]>} Promise resolving to an array of favorite symbol strings
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const favorites = await growwConnector.getFavorites();
   * console.log('My favorite stocks:', favorites);
   */
  async getFavorites(): Promise<string[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/favorites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch favorites')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch favorites:', error)
      return []
    }
  }

  /**
   * Get WebSocket URL for real-time data
   * Returns the URL needed to establish a WebSocket connection for streaming market data
   * 
   * @returns {string} The WebSocket URL string
   * 
   * @example
   * const wsUrl = growwConnector.getWebSocketUrl();
   * const ws = new WebSocket(wsUrl);
   */
  getWebSocketUrl(): string {
    // This is a placeholder - Groww might have a different WebSocket URL
    return 'wss://stream.groww.in/ws'
  }

  /**
   * Get the current access token
   * Provides the token needed for API authentication
   * 
   * @returns {Promise<string>} Promise resolving to the access token
   * 
   * @example
   * const token = await growwConnector.getAccessToken();
   */
  async getAccessToken(): Promise<string> {
    // Groww doesn't use access tokens in the same way as Zerodha
    // Return the API key as the access token for compatibility
    return this.apiKey;
  }

  /**
   * Get the API key
   * Provides the API key used for authentication
   * 
   * @returns {Promise<string>} Promise resolving to the API key
   * 
   * @example
   * const apiKey = await growwConnector.getApiKey();
   */
  async getApiKey(): Promise<string> {
    return this.apiKey;
  }

  /**
   * Get list of tradable instruments
   * Retrieves the list of instruments available for trading
   * 
   * @param {string} [exchange] Optional exchange to filter instruments by (NSE, BSE, etc.)
   * @returns {Promise<any[]>} Promise resolving to an array of instruments
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get all instruments
   * const allInstruments = await growwConnector.getInstruments();
   * 
   * // Get only NSE instruments
   * const nseInstruments = await growwConnector.getInstruments('NSE');
   */
  async getInstruments(exchange?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww');
    }

    try {
      // Use our server-side API endpoint to fetch instruments
      const response = await fetch(`/api/connectors/groww/instruments${exchange ? `/${exchange}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch instruments');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch instruments:', error);
      return [];
    }
  }

  /**
   * Get market quotes for specified symbols
   * Retrieves detailed market quotes including bid/ask prices, volumes, etc.
   * 
   * @param {string[]} symbols Array of symbols to get quotes for
   * @returns {Promise<any>} Promise resolving to quotes data
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const quotes = await growwConnector.getQuotes(['RELIANCE', 'TCS', 'INFY']);
   */
  async getQuotes(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww');
    }

    try {
      // Use our server-side API endpoint to fetch quotes
      const response = await fetch('/api/connectors/groww/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch quotes');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      return {};
    }
  }

  /**
   * Get OHLC quotes for specified symbols
   * Retrieves open, high, low, close data for the symbols
   * 
   * @param {string[]} symbols Array of symbols to get OHLC data for
   * @returns {Promise<any>} Promise resolving to OHLC data
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const ohlc = await growwConnector.getOHLC(['RELIANCE', 'TCS', 'INFY']);
   */
  async getOHLC(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww');
    }

    try {
      // Use our server-side API endpoint to fetch OHLC data
      const response = await fetch('/api/connectors/groww/ohlc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch OHLC data');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch OHLC data:', error);
      return {};
    }
  }

  /**
   * Get LTP (Last Traded Price) for specified symbols
   * Retrieves the last traded price for the symbols
   * 
   * @param {string[]} symbols Array of symbols to get LTP for
   * @returns {Promise<any>} Promise resolving to LTP data
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * const ltp = await growwConnector.getLTP(['RELIANCE', 'TCS', 'INFY']);
   */
  async getLTP(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww');
    }

    try {
      // Use our server-side API endpoint to fetch LTP data
      const response = await fetch('/api/connectors/groww/ltp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch LTP data');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch LTP data:', error);
      return {};
    }
  }

  /**
   * Get options chain data for a specific symbol and expiry
   * Retrieves options chain with strike prices, premiums, and other data
   * 
   * @param {string} symbol The underlying symbol (e.g., 'NIFTY', 'BANKNIFTY')
   * @param {string} [expiry] Optional expiry date (uses nearest expiry if omitted)
   * @returns {Promise<OptionsChainResponse>} Promise resolving to options chain data
   * @throws {Error} If not connected to Groww
   * 
   * @example
   * // Get options chain for NIFTY with default expiry
   * const niftyOptions = await growwConnector.getOptionsChain('NIFTY');
   * 
   * // Get options chain for BANKNIFTY with specific expiry
   * const bankNiftyOptions = await growwConnector.getOptionsChain('BANKNIFTY', '2023-06-29');
   */
  async getOptionsChain(symbol: string, expiry?: string): Promise<OptionsChainResponse> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/groww/optionschain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          symbol: symbol,
          expiry: expiry
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch options chain')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch options chain:', error)
      // Return a default empty response
      return {
        expiryDates: [],
        optionsChain: []
      }
    }
  }

  /**
   * Cancel an open order on Groww
   * Cancels a specific order by its order ID
   * 
   * @param {string} orderId The order ID to cancel
   * @param {string} [symbol] Optional symbol parameter for the order
   * @returns {Promise<boolean>} Promise resolving to true if cancellation was successful
   * @throws {Error} If not connected to Groww or cancellation fails
   * 
   * @example
   * // Cancel an order by its ID
   * const success = await growwConnector.cancelOrder('GR123456789');
   * if (success) {
   *   console.log('Order cancelled successfully');
   * }
   */
  async cancelOrder(orderId: string, symbol?: string): Promise<boolean> {
    if (!this.isConnected) {
      throw new Error('Not connected to Groww')
    }

    try {
      if (!orderId) {
        throw new Error('Order ID is required to cancel an order')
      }

      // Mock implementation for Groww - to be implemented later
      console.log('Mock: Cancelling Groww order:', { orderId, symbol })
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock successful cancellation
      console.log(`Mock: Groww order ${orderId} cancelled successfully`)
      return true
    } catch (error) {
      console.error('Failed to cancel Groww order:', error)
      return false
    }
  }
}